/**
 * @fileOverview Analytics – link and error tracking
 * @name Analytics Widget
 * @author dquock, jamuferguson
 *
 * This file is a wrapper around site catalyst and fpti
 * and allows us to easily fire off tracking calls to
 * both at the same time.
 *
 * Access FPTI performance info from PAYPAL.analytics.perf
 * https://dev.paypal.com/wiki/General/FPTI-PerformanceTracking
 *
 */

import log from 'loglevel'
import _ from 'lodash'

import { tracking } from './mParticle'
import Events from './eventTags'
import serverProps from '../../transfer/utils/serverProps'
const { analyticsFlow, recipientData = {} } = serverProps

const RECORDING_ANALYTICS = 'Recording analytics:'

// NOTE: it's not safe to shadow window.fpti at the module level
// because it gets reset after 500ms in pa.js (see: pageTracking.dust)

// retries for loading pa.js
let retries = 0

const PAYPAL = window.PAYPAL || {}

// New environment will be stored, incase want to overrite default env for any external flows.
let newEnv

// instantiate the view (with a backup for mocks mode)
const emptyAnalytics = {
  logActivity() {},
  recordClick() {},
  recordImpression() {},
  record() {},
}
export let analytics = emptyAnalytics

let isInitialized = false
analyticsReady(() => {
  analytics =
    PAYPAL.analytics &&
    PAYPAL.analytics.Analytics &&
    new PAYPAL.analytics.Analytics()
  isInitialized = true
})

/*************
 * Public API
 *************/

/**
 Setting new environment
 * @param {String} env - new environment set by external flow.
 */
export function overrideEnvironment(env) {
  newEnv = env
}

/**
 * Set FPTI/SC variables and fire tracking beacons
 *
 * @param {String} linkName - The name of the link
 * @param {String} pageGroup - The page group (prev. pageName)
 * @param {String} pageName - The actual page name (prev. pageName2)
 * @param {String} trackType - i.e "link"
 * @param {String} transactionDetailsLinks
 */
export const trackLink = whenReady(
  // eslint-disable-next-line max-params
  (
    linkName,
    pageGroup,
    pageName,
    trackType,
    transactionDetailsLinks,
    data = {}
  ) => {
    const fpti = window.fpti || {}
    const finalPageGroup = newEnv
      ? getPageNameForEnvironment(pageGroup, newEnv)
      : pageGroup
    const finalPageName = newEnv
      ? getPageNameForEnvironment(pageName, newEnv)
      : pageName

    log.info('Recording analytics:', [
      linkName,
      finalPageGroup,
      finalPageName,
      trackType,
      transactionDetailsLinks,
      data,
    ])

    // set linkname
    fpti.link = linkName

    // set pageNames
    fpti.page = finalPageName

    // Only change pglk if linkName exists and was clicked on, to persist current pglk until next linkName
    if (linkName) {
      fpti.pglk = `${pageGroup}|${linkName}`
    }

    fpti.pgrp = finalPageGroup

    clearErrors(fpti)

    try {
      if (trackType === 'link') {
        analytics.recordClick({ data: data })
      } else {
        analytics.recordImpression({ data: data })
      }
    } catch (exception) {
      log.info('Analytics Exception Found:', exception)
    }
  }
)

/**
 * Wrapper around trackLink that lets us send additional FPTI keys
 * @param linkName - the link name for the trackLink call
 * @param pageGroup - the page group for the trackLink call
 * @param {String} pageName - The actual page name (prev. pageName2)
 * @param data - key value pairs we want to send to FPTI then remove
 */
export const trackLinkWithData = whenReady(
  // eslint-disable-next-line max-params
  (linkName, pageGroup, pageName, data, trackType, transactionDetailsLinks) => {
    const fpti = window.fpti || {}

    // apply extra fields to window.fpti
    Object.assign(fpti, data)

    trackLink(
      linkName,
      pageGroup,
      pageName,
      trackType,
      transactionDetailsLinks,
      data
    )

    // remove those additional keys
    Object.keys(data).forEach(key => {
      delete fpti[key]
    })
  }
)

/**
 * Link tracking
 * @param {Object} event - click event || the clicked jQuery object of the input used to submit a form
 * if event isn't defined or is null, explicitly specify params
 * @param {String} linkName - name attribute from the link
 * @param {String} pageGroup - data-pagename the link is going to
 * @param {String} pageName - data-pagename2 the link is going to
 * @param {String} trackType - page-level or not
 * @param {String} transactionDetailsLinks - activity transaction details links
 */
export const trackElement = whenReady($element => {
  const linkName = $element.attr('name') || ($element && $element.html())
  const trackType = $element.data('track-type') || 'link'

  const pageGroup = $element.data('pagename')
  const pageName = $element.data('pagename2') || `${pageGroup}:::${trackType}`

  const nextPageGroup = $element.data('nextpagegroup')
  const nextPageName = $element.data('nextpagename')

  const transactionDetailsLinks = $element.data('transactiondetailslinks')
  const extra = $element.data('extra') || {}

  // Don't want beacons firing without a destination
  if (pageGroup) {
    trackLinkWithData(
      linkName,
      pageGroup,
      pageName,
      extra,
      trackType,
      transactionDetailsLinks
    )
  }

  // Don't want CPLTracking unless next pages are specified
  if (nextPageGroup) {
    startCPLTracking(nextPageGroup, nextPageName, extra)
  }
})

/**
 * Track Impression
 * Impression beacon will be fired when `trackType` is not specified
 * @param {string} error - The page name that fires impression beacon
 */
export const trackImpression = whenReady((pageGroup, pageName, data = {}) => {
  log.info('Recording Page Impression:', [pageGroup, pageName, data])

  const fpti = window.fpti || {}

  // set pageGroup / pageName
  fpti.pgrp = pageGroup
  fpti.page = pageName

  analytics.recordImpression({ data: data, trackCPL: true })
})

export const trackActivity = whenReady((pageGroup, pageName, data = {}) => {
  log.info('Recording Activity:', [pageGroup, pageName, data])

  const fpti = window.fpti || {}

  // set pageGroup / pageName
  fpti.pgrp = pageGroup
  fpti.page = pageName
  var activityData = {
    page: fpti.page,
    pgrp: fpti.pgrp,
    comp: fpti.comp,
    ...data,
  }
  analytics.logActivity(activityData)
})

/**
 *  Tracks errors on the page such as on a form
 *  @param {Object} error - The object containing the props to set
 */
export const trackError = whenReady(options => {
  const fpti = window.fpti || {}

  log.info(RECORDING_ANALYTICS, options)

  // set error props
  fpti.erpg = options.message // error message
  fpti.erfd = options.fieldId // form field with error (field1|field2|field3)
  fpti.eccd = options.code // error code
  fpti.calc = options.debugId || fpti.calc // correlation id if one was supplied in options

  analytics.recordImpression()
})

/**
 * Log client-side JS errors
 * @param error {Error} A valid JS Error (sometimes null watch out)
 * @param source {string} filename/line/col "file.js 23:39"
 */
export const trackJSError = whenReady((error, source) => {
  if (!PAYPAL.analytics) {
    return
  }
  var fpti = window.fpti || {}
  var data = {
    page: fpti.page,
    pgrp: fpti.pgrp,
    comp: fpti.comp,
    erpg: (error && error.message) || 'Script error',
    // uicomp: '',
    // error_code: '',
    error_type: 'WINDOW_ONERROR',
    error_description: getShortStack(error),
    error_source: source,
  }
  log.info('Logging JS Error', data)

  // see: https://engineering.paypalcorp.com/confluence/display/FPTI/Client-Side+JavaScript+Implementation#Client-SideJavaScriptImplementation-SubmittingClient-sideerrors
  analytics.logActivity(data)
})

/**
 * Log instances of missing content
 * Strip out `key` and `url` from `missingContentData` since they will be included in different fields
 * @param missingData {object} e.g. { key: 'US/en/transfer/recipient/header.title', url: 'myaccount/transfer/send', stack: '...', contentSize: 100 }
 */
export const trackMissingContent = whenReady(
  ({ key, url, ...missingContentData }) => {
    if (!PAYPAL.analytics) {
      return
    }
    var fpti = window.fpti || {}
    var data = {
      page: fpti.page,
      pgrp: fpti.pgrp,
      comp: fpti.comp,
      erpg: key,
      error_type: 'MISSING_CONTENT',
      error_description: JSON.stringify(missingContentData),
      error_source: url,
    }
    log.info('Logging instance of missing content', data)

    // see: https://engineering.paypalcorp.com/confluence/display/FPTI/Client-Side+JavaScript+Implementation#Client-SideJavaScriptImplementation-SubmittingClient-sideerrors
    analytics.logActivity(data)
  }
)

/**
 *  Tracks link on a page
 *  keeps the pagenames the same, but tracks the link
 *  @param {String} linkName linkName to track
 */
export const trackAction = whenReady(linkName => {
  const fpti = window.fpti || {}
  log.info(RECORDING_ANALYTICS, linkName)

  // set linkname
  fpti.link = linkName
  fpti.pglk = `${fpti.pgrp}|${linkName}`

  clearErrors(fpti)
  analytics.recordClick()
})

/**
 * Simple back button tracking
 */
export function trackBackButton() {
  trackAction('back')
  tracking(Events.p2p_physical_back_button_pressed({}))
}

/**************
 * Private API
 **************/

/**
 * Get a reasonably short stack trace so we don't hit issues
 * with going over the limit of chars allowed in an FPTI GET
 */
export function getShortStack(error) {
  const stack = error && error.stack

  // Tries to remove the http://www.paypalobjects.com/ stuff from the stack traces.
  return (
    (stack && stack.replace(/http.*\/(\w+.\.js)/g, '$1').slice(0, 500)) || ''
  )
}

// clear out error props, in case they exist
function clearErrors(fpti) {
  fpti.erpg = undefined // error message
  fpti.erfd = undefined // form field with error (field1|field2|field3)
  fpti.eccd = undefined // error code
}

/**
 * Updating name with new environment
 * @param {String} env - new environment set by external flow.
 * @param {String} name - pageGroup or pageName
 */
export function getPageNameForEnvironment(name, env) {
  if (env) {
    const nameArray = name.split(':')
    nameArray[2] = env
    return nameArray.join(':')
  }
  return name
}

/**
 * PA.js has a 500ms delay between its inception and setup initialization, and we need to know when
 * the setup is really complete before firing our analytic calls. PA.js allows us to overwrite their
 * noop setupComplete function with our own function, which we do in pageTracking.dust -
 * Our logic simply specifies that when setupComplete is invoked, set _.isReady to true.
 */
function analyticsReady(callback, requiresInitialization = false) {
  const analyticsIsReady = _.get(PAYPAL, 'analytics._isReady')
  if (requiresInitialization) {
    if (isInitialized) {
      return callback()
    }
  } else if (analyticsIsReady) {
    return callback()
  }
  // wait up to 150s for analytics to initialize
  // we give up analytics eventually
  retries++
  if (retries > 1000) {
    return
  }
  return setTimeout(() => analyticsReady(callback, requiresInitialization), 150)
}

// sets the flow type (engagment module, funnel, etc..)
;(function setAnalyticsFlow() {
  var analyticsIsReady = _.get(PAYPAL, 'analytics._isReady') && window.fpti

  if (analyticsIsReady) {
    window.fpti.flow = analyticsFlow
    window.fpti.pp_flow = recipientData.env
    window.fpti.slug_name =
      recipientData.recipientIdType === 'ppme' && recipientData.recipientId
    return
  }
  setTimeout(() => setAnalyticsFlow(), 150)
})()

/**
 * Wrap any anonymous function in the analyticsReady function
 */
export function whenReady(fn) {
  return (...args) => analyticsReady(() => fn(...args), true)
}

/**************
 * Clientside performance tracking (CPL)
 **************/

// Currently, end tracking is called where getting the page name is not easy
// Page names need to be passed to track impressions. This is a hack grabbing the page name
// when start tracking is called and setting to last lastPageName in order to pass it to endCPLTracking.
export function startCPLTracking(pageGroup, pageName, data = {}) {
  if (_.get(PAYPAL, 'analytics.startCPLTracking')) {
    let options = {}
    const { ulData: { fnSessionId: sessionID = '' } = {} } = PAYPAL
    if (pageName && pageGroup) {
      options = { flid: sessionID, pgrp: pageGroup, page: pageName }
    }
    trackImpression(pageGroup, pageName, data)
    // creating this key on PAYPAL.analytics to store our beacon info
    // becauase we call endCPLTracking with no arguments
    PAYPAL.analytics.beaconTrackingData = data
    PAYPAL.analytics.startCPLTracking(options)
  }
}

// Sometimes we don't have the data necessary for the next page's impression,
// because startCPLTracking begins at the time of click from a prior page.
export function addCPLData(data) {
  if (_.get(PAYPAL, 'analytics.beaconTrackingData')) {
    PAYPAL.analytics.beaconTrackingData = {
      ...PAYPAL.analytics.beaconTrackingData,
      ...data,
    }
  }
}

// Sets beacon data points directly on fpti - data points will persist until overwritten
export function setPersistentBeaconData(key, value) {
  if (window.fpti) {
    window.fpti[key] = value
  }
}

/**
 * persists data for later usage
 * @param {String} key - key to store within PAYPAL.analytics
 * @param {Object} data - new data to be stored
 */
export function setPersistentData(key, data) {
  if (!PAYPAL.analytics) {
    return
  }
  if (key) {
    PAYPAL.analytics[key] = {
      ...PAYPAL.analytics[key],
      ...data,
    }
  }
}

/**
 * gets persisted data that we stored previously
 * @param {String} key - key in which the data is stored
 * @returns {Object} data - stored data
 */
export function getPersistentData(key) {
  if (!PAYPAL.analytics) {
    return
  }
  if (key) {
    return PAYPAL.analytics[key]
  }
}

/**
 * removes persisted data that is no longer needed
 * @param {String} key - key to removed within PAYPAL.analytics
 * @param {Object} data - (optional) data to be removed
 */
export function removePersistentData(key, data) {
  if (!PAYPAL.analytics) {
    return
  }
  if (key && data) {
    PAYPAL.analytics[key] = _.pickBy(PAYPAL.analytics[key], function(v, k) {
      const keysToBeRemoved = Object.keys(data)
      return !keysToBeRemoved.includes(k)
    })
  } else if (key) {
    PAYPAL.analytics = _.omit(PAYPAL.analytics, key)
  }
}

export function endCPLTracking(pageGroup, pageName, data = {}) {
  // End tracking is only called if start tracking has been called. `cpl` and `cpl.started` are
  // only available after start tracking call.
  const CPLTrackingHasStarted = _.get(PAYPAL, 'analytics.cpl.started')
  const endCPLTrackingExists = _.get(PAYPAL, 'analytics.endCPLTracking')
  if (CPLTrackingHasStarted && endCPLTrackingExists) {
    const beaconTrackingData = PAYPAL.analytics.beaconTrackingData || {} // our personal beacon storage created in startCPLTracking
    const beaconData = PAYPAL.analytics.cpl.beaconData || {} // Where the beacons are fired from
    PAYPAL.analytics.cpl.beaconData = { ...beaconData, ...beaconTrackingData }

    let options = {}
    const { ulData: { fnSessionId: sessionID = '' } = {} } = PAYPAL
    if (pageName && pageGroup) {
      options = {
        pageData: { flid: sessionID, pgrp: pageGroup, page: pageName },
      }
    }
    PAYPAL.analytics.endCPLTracking(options)

    // We add data to our tracking calls, but CPL persisted the data by dropping it on window.FPTI
    // We don't want ALL previous tracking data on all future calls, so let's pluck out the data we just used
    window.fpti = _.pickBy(window.fpti, function(value, key) {
      var addedBeaconKeys = Object.keys(beaconTrackingData)
      return !addedBeaconKeys.includes(key)
    })
  }
}
